import { useInfiniteQuery } from '@tanstack/react-query';
import { axiosInstance } from '../../utils';
import { AiChat } from '../../@types';

interface Payload {
  name?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  filters?: { [key: string]: any };
}

export const getAiChats = async ({
  name,
  page,
  limit,
  sortBy,
  sortOrder,
  filters = {},
}: Payload): Promise<AiChat[]> => {
  const searchParams = new URLSearchParams();

  if (name) searchParams.append('name', name.toString());
  if (page) searchParams.append('page', page.toString());
  if (limit) searchParams.append('limit', limit.toString());
  if (sortBy) searchParams.append('sortBy', sortBy);
  if (sortOrder) searchParams.append('sortOrder', sortOrder);

  Object.entries(filters).forEach(([key, value]) => {
    if (value) {
      searchParams.append(`filters[${key}]`, value);
    }
  });

  const res = await axiosInstance.get('/api/script/get-ai-chats', { params: searchParams });
  return res.data;
};

export const useGetAiChats = (params?: Payload) => {
  const normalizedParams: Payload | undefined = params
    ? {
        ...params,
        // Ensure we don't put unstable empty objects into the query key.
        filters:
          params.filters && Object.keys(params.filters).length > 0 ? params.filters : undefined,
      }
    : undefined;

  return useInfiniteQuery(
    ['getAiChats', normalizedParams],
    ({ pageParam = 1 }) => getAiChats({ ...normalizedParams, page: pageParam }),
    {
      getNextPageParam: (lastPage, allPages) => {
        const currentPage = allPages.length;
        const currentPageDataLength = allPages[currentPage - 1].length;

        if (currentPageDataLength === normalizedParams?.limit) {
          return currentPage + 1;
        } else {
          return undefined;
        }
      },
      staleTime: 60_000,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );
};
